import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  Button,
  Stack,
  Chip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import axios from "../../../config/axiosConfig";
import { useAuth } from "../../../context/AuthContext";
import MuiAlert from "@mui/material/Alert";
import CentroEquipoDetalleDialog from "../../requerimiento/components/CentroEquipoDetalleDialog";

// Estado 3 (Asignado) de una OT de Reemplazo: espejo de indexad.php + cargaDetalle.php
// + guarda.php + actualiza_cambiar*.php, rediseñado a pedido del usuario para que se
// sienta menos largo — en vez de un catálogo genérico separado de la lista de lo
// retirado, esta pantalla lista directamente los equipos marcados para reemplazo en el
// paso 1 (SeleccionarReemplazoOT.jsx) y, al hacer click en uno, abre un panel al fondo
// (mismo patrón ya validado con el usuario en DiagnosticoMantenimientoOT.jsx) para
// buscar y asignar su reemplazo puntual. El equipo nuevo NO genera historial acá — a
// pedido del usuario, recién se crea cuando se Finaliza el Reemplazo (backend:
// otReemplazoModel.finalizarReemplazoTx). El emparejamiento retirado↔nuevo viaja como
// marcador en la observación del RETIRADO, ya resuelto por el backend en `pareja`.
const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};
const tableSx = { "& .MuiTableCell-root": { py: 0.25, px: 0.6, fontSize: 11 } };
const controlSx = {
  "& .MuiInputLabel-root": { fontSize: 11 },
  "& .MuiOutlinedInput-root": { height: 30, fontSize: 11.5 },
};

const TIPO_LABEL = { RADIO: "Radio", BATERIA: "Batería", VARIOS: "Equipo" };
const PAGE_SIZE = 10;
const ESTADOS_FINALES = ["Inactivo", "Mantenimiento", "Baja", "Robado"];
// Igual que index.php: TKR arranca con "Mantenimiento" preseleccionado, "otros" con "Inactivo".
const ESTADO_DEFAULT = { RADIO: "Mantenimiento", BATERIA: "Mantenimiento", VARIOS: "Inactivo" };

const endpointDeFamilia = (familia) => {
  if (familia === "RADIO") return "/api/equipos-radio";
  if (familia === "BATERIA") return "/api/equipos-bateria";
  return "/api/equipos-varios";
};

const keyDe = (row) => `${row.familia}-${row.identificador}`;

const TIPOS_TKR = ["RADIO", "BATERIA"];
const TIPOS_OTROS = ["VARIOS"];

const AsignarReemplazoOT = ({ cod_ot, cod_cliente, cod_servicio_cliente, esTKR, onEquiposActualizados }) => {
  const { user } = useAuth();
  const [retirados, setRetirados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [equipoDialog, setEquipoDialog] = useState(null);
  const [deshaciendo, setDeshaciendo] = useState(null);
  const [quitando, setQuitando] = useState(null);

  const [selected, setSelected] = useState(null); // fila de `retirados` con panel abierto
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [buscar, setBuscar] = useState("");
  const [buscarAplicado, setBuscarAplicado] = useState("");
  const [loadingItems, setLoadingItems] = useState(false);
  const [extra, setExtra] = useState({ grupo: "", placa: "", id_ref: "" });
  const [clasificacion, setClasificacion] = useState({ estadoFinal: "", observacion: "" });
  const [asignando, setAsignando] = useState(null);
  const panelRef = React.useRef(null);

  // Agregar otro equipo a reemplazar (la OT ya está en "Asignado" — el paso 1 no está
  // más disponible, así que esto vive acá para cuando el técnico se da cuenta después).
  const tiposAgregar = esTKR ? TIPOS_TKR : TIPOS_OTROS;
  const [agregarAbierto, setAgregarAbierto] = useState(false);
  const [agregarTipo, setAgregarTipo] = useState(tiposAgregar[0]);
  const [agregarItems, setAgregarItems] = useState([]);
  const [agregarTotal, setAgregarTotal] = useState(0);
  const [agregarPage, setAgregarPage] = useState(1);
  const [agregarBuscar, setAgregarBuscar] = useState("");
  const [agregarBuscarAplicado, setAgregarBuscarAplicado] = useState("");
  const [agregarLoading, setAgregarLoading] = useState(false);
  const [agregando, setAgregando] = useState(null);
  const agregarTotalPages = Math.max(1, Math.ceil(agregarTotal / PAGE_SIZE));

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const cargar = useCallback(() => {
    setLoading(true);
    setError("");
    axios.get(`/api/ot-reemplazo/retirados/${cod_ot}`)
      .then((res) => setRetirados(res.data || []))
      .catch(() => setError("No se pudo cargar el estado del reemplazo."))
      .finally(() => setLoading(false));
  }, [cod_ot]);

  useEffect(() => { cargar(); }, [cargar]);

  const cargarDisponibles = (familia, nextPage = 1, buscarTexto = "") => {
    setLoadingItems(true);
    axios.get(endpointDeFamilia(familia), {
      params: { estado: "Inactivo", ciudad: user?.sucursal, buscar: buscarTexto, page: nextPage, pageSize: PAGE_SIZE },
    })
      .then((res) => {
        const data = res.data || {};
        setItems((data.items || []).map((r) => ({ key: r.ID, identificador: r.serie, modelo: r.modelo, ciudad: r.ciudad })));
        setTotal(Number(data.total || 0));
        setPage(Number(data.page || nextPage));
      })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoadingItems(false));
  };

  const abrirBuscador = (retirado) => {
    setAgregarAbierto(false);
    setSelected(retirado);
    setBuscar("");
    setBuscarAplicado("");
    setExtra({ grupo: retirado.grupo || "", placa: retirado.placa || "", id_ref: retirado.id_ref || "" });
    setClasificacion({ estadoFinal: ESTADO_DEFAULT[retirado.familia] || "Inactivo", observacion: "" });
    cargarDisponibles(retirado.familia, 1, "");
    requestAnimationFrame(() => panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  const cargarDisponiblesAgregar = (tipo, nextPage = 1, buscarTexto = "") => {
    setAgregarLoading(true);
    axios.get(endpointDeFamilia(tipo), {
      params: { estado: "Activo", cod_servicio: cod_servicio_cliente, buscar: buscarTexto, page: nextPage, pageSize: PAGE_SIZE },
    })
      .then((res) => {
        const data = res.data || {};
        setAgregarItems((data.items || []).map((r) => ({ key: r.ID, identificador: r.serie, modelo: r.modelo })));
        setAgregarTotal(Number(data.total || 0));
        setAgregarPage(Number(data.page || nextPage));
      })
      .catch(() => { setAgregarItems([]); setAgregarTotal(0); })
      .finally(() => setAgregarLoading(false));
  };

  const abrirAgregar = () => {
    setSelected(null);
    setAgregarAbierto(true);
    setAgregarBuscar("");
    setAgregarBuscarAplicado("");
    cargarDisponiblesAgregar(agregarTipo, 1, "");
    requestAnimationFrame(() => panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  const handleBuscarAgregar = () => {
    setAgregarBuscarAplicado(agregarBuscar.trim());
    cargarDisponiblesAgregar(agregarTipo, 1, agregarBuscar.trim());
  };

  const handleAgregar = async (row) => {
    setAgregando(row.key);
    setError("");
    try {
      await axios.post("/api/ot-reemplazo/agregar-retirado", {
        cod_ot,
        cod_cliente,
        familia: agregarTipo,
        identificador: row.identificador,
      });
      cargar();
      cargarDisponiblesAgregar(agregarTipo, agregarPage, agregarBuscarAplicado);
      if (onEquiposActualizados) onEquiposActualizados();
    } catch (err) {
      setError(err?.response?.data?.error || `No se pudo agregar "${row.identificador}".`);
    } finally {
      setAgregando(null);
    }
  };

  const handleBuscar = () => {
    if (!selected) return;
    setBuscarAplicado(buscar.trim());
    cargarDisponibles(selected.familia, 1, buscar.trim());
  };

  const handleAsignar = async (row) => {
    if (!selected) return;
    setAsignando(row.key);
    setError("");
    try {
      await axios.post("/api/ot-reemplazo/asignar", {
        cod_ot,
        familia: selected.familia,
        identificador: row.identificador,
        cod_cliente,
        cod_servicio_cliente,
        grupo: extra.grupo || "",
        placa: extra.placa || "",
        id_ref: extra.id_ref || "",
        reemplazaIdentificador: selected.identificador,
        estadoFinalRetirado: clasificacion.estadoFinal,
        observacionRetirado: clasificacion.observacion || "",
      });
      setSelected(null);
      cargar();
      if (onEquiposActualizados) onEquiposActualizados();
    } catch (err) {
      setError(err?.response?.data?.error || `No se pudo asignar "${row.identificador}".`);
    } finally {
      setAsignando(null);
    }
  };

  const handleDeshacer = async (retirado) => {
    const pareja = retirado.pareja;
    if (!pareja) return;
    if (!window.confirm(`¿Deshacer el reemplazo? "${pareja.identificador}" vuelve a Inactivo.`)) return;
    setDeshaciendo(keyDe(retirado));
    setError("");
    try {
      await axios.post("/api/ot-reemplazo/deshacer", { cod_ot, familia: retirado.familia, identificador: pareja.identificador });
      cargar();
      if (onEquiposActualizados) onEquiposActualizados();
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo deshacer el reemplazo.");
    } finally {
      setDeshaciendo(null);
    }
  };

  const handleQuitar = async (retirado) => {
    if (!window.confirm(`¿Quitar "${retirado.identificador}" de la lista de equipos a reemplazar? Vuelve a Activo con el cliente.`)) return;
    setQuitando(keyDe(retirado));
    setError("");
    try {
      await axios.post("/api/ot-reemplazo/quitar-retirado", { cod_ot, cod_cliente, cod_servicio_cliente, familia: retirado.familia, identificador: retirado.identificador });
      if (selected && keyDe(selected) === keyDe(retirado)) setSelected(null);
      cargar();
      if (onEquiposActualizados) onEquiposActualizados();
    } catch (err) {
      setError(err?.response?.data?.error || `No se pudo quitar "${retirado.identificador}".`);
    } finally {
      setQuitando(null);
    }
  };

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={22} /></Box>;
  }

  return (
    <Box sx={{ mt: 1 }}>
      {error && <MuiAlert severity="error" variant="outlined" sx={{ mb: 1, fontSize: 11, py: 0 }}>{error}</MuiAlert>}

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          Equipos a Reemplazar
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon fontSize="small" />}
          onClick={abrirAgregar}
          sx={{ minHeight: 24, height: 24, fontSize: 10.5 }}
        >
          Agregar equipo a reemplazar
        </Button>
      </Stack>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, borderRadius: 0.5 }}>
        <Table size="small" sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Tipo</TableCell>
              <TableCell sx={headCellSx}>Serie</TableCell>
              <TableCell sx={headCellSx}>Modelo</TableCell>
              <TableCell sx={{ ...headCellSx, width: 110 }}>Estado Final</TableCell>
              <TableCell sx={headCellSx}>Observación</TableCell>
              <TableCell sx={{ ...headCellSx, width: 220 }}>Reemplazo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!retirados.length ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    No hay equipos marcados para reemplazo en esta OT.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              retirados.map((row) => {
                const pareja = row.pareja;
                const rowKey = keyDe(row);
                const seleccionada = selected && keyDe(selected) === rowKey;
                return (
                  <TableRow key={rowKey} hover selected={seleccionada}>
                    <TableCell>{TIPO_LABEL[row.familia]}</TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        onClick={() => setEquipoDialog({ tipo: row.familia.toLowerCase(), identificador: row.identificador })}
                        sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
                      >
                        {row.identificador}
                      </Box>
                    </TableCell>
                    <TableCell>{row.modelo}</TableCell>
                    <TableCell>{pareja ? row.estadoFinal : <Chip size="small" label="Pendiente" sx={{ height: 18, fontSize: 10 }} />}</TableCell>
                    <TableCell>{pareja ? (row.observacion || "-") : "-"}</TableCell>
                    <TableCell>
                      {pareja ? (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Chip
                            size="small"
                            color="success"
                            variant="outlined"
                            label={`${pareja.identificador} (${pareja.modelo})`}
                            onClick={() => setEquipoDialog({ tipo: row.familia.toLowerCase(), identificador: pareja.identificador })}
                            sx={{ height: 20, fontSize: 10.5, cursor: "pointer" }}
                          />
                          {/* Igual criterio que "Quitar": en TKR se saca a pedido del
                              usuario para no dar forma de esquivar la asignación ya
                              hecha; en "otros" servicios sigue disponible. */}
                          {!esTKR && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              disabled={deshaciendo === rowKey}
                              onClick={() => handleDeshacer(row)}
                              sx={{ minHeight: 20, height: 20, fontSize: 10 }}
                            >
                              {deshaciendo === rowKey ? <CircularProgress size={12} /> : "Deshacer"}
                            </Button>
                          )}
                        </Stack>
                      ) : (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={() => abrirBuscador(row)}
                            sx={{ minHeight: 22, height: 22, fontSize: 10.5 }}
                          >
                            Buscar Reemplazo
                          </Button>
                          {/* Para TKR se saca a pedido del usuario: la cuenta de
                              finalizarAsignacionTx exige reemplazo para cada radio
                              retirada, y "Quitar" permitiría esquivar esa validación en
                              vez de resolverla. En "otros" servicios esa cuenta no existe,
                              así que ahí sigue disponible. */}
                          {!esTKR && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              disabled={quitando === rowKey}
                              onClick={() => handleQuitar(row)}
                              sx={{ minHeight: 22, height: 22, fontSize: 10.5 }}
                            >
                              {quitando === rowKey ? <CircularProgress size={12} /> : "Quitar"}
                            </Button>
                          )}
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {selected && (
        <Paper ref={panelRef} variant="outlined" sx={{ mb: 2, borderColor: "primary.main" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Buscando reemplazo para {selected.identificador} ({selected.modelo})
            </Typography>
            <IconButton size="small" onClick={() => setSelected(null)}><CloseIcon fontSize="small" /></IconButton>
          </Stack>

          <Box sx={{ px: 1.5, pb: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
              ¿Qué pasa con {selected.identificador}?
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
              <Select
                size="small"
                value={clasificacion.estadoFinal}
                onChange={(e) => setClasificacion((p) => ({ ...p, estadoFinal: e.target.value }))}
                sx={{ width: 150, fontSize: 11.5, height: 30 }}
              >
                {ESTADOS_FINALES.map((op) => (
                  <MenuItem key={op} value={op} sx={{ fontSize: 11 }}>{op}</MenuItem>
                ))}
              </Select>
              <TextField
                size="small"
                label="Observación"
                value={clasificacion.observacion}
                onChange={(e) => setClasificacion((p) => ({ ...p, observacion: e.target.value }))}
                fullWidth
                sx={controlSx}
              />
            </Stack>

            {selected.familia === "RADIO" && (
              <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                <TextField size="small" label="Grupo" value={extra.grupo} onChange={(e) => setExtra((p) => ({ ...p, grupo: e.target.value }))} sx={{ width: 110, ...controlSx }} />
                <TextField size="small" label="Placa" value={extra.placa} onChange={(e) => setExtra((p) => ({ ...p, placa: e.target.value }))} sx={{ width: 110, ...controlSx }} />
                <TextField size="small" label="ID Cliente" value={extra.id_ref} onChange={(e) => setExtra((p) => ({ ...p, id_ref: e.target.value }))} sx={{ width: 130, ...controlSx }} />
              </Stack>
            )}

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <TextField
                size="small"
                label="Buscar"
                value={buscar}
                onChange={(e) => setBuscar(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleBuscar(); }}
                sx={{ minWidth: 160, ...controlSx }}
              />
              <IconButton size="small" onClick={handleBuscar} title="Buscar"><SearchIcon fontSize="small" /></IconButton>
              <Chip size="small" label={`Total: ${total}`} sx={{ height: 20, fontSize: 11 }} />
            </Stack>

            <TableContainer component={Paper} variant="outlined" sx={{ mb: 0.5, maxHeight: 280, borderRadius: 0.5 }}>
              <Table size="small" stickyHeader sx={tableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Identificador</TableCell>
                    <TableCell sx={headCellSx}>Modelo</TableCell>
                    <TableCell sx={headCellSx}>Ciudad</TableCell>
                    <TableCell sx={{ ...headCellSx, width: 90 }}>Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingItems ? (
                    <TableRow><TableCell colSpan={4} align="center" sx={{ py: 1.5 }}><CircularProgress size={16} /></TableCell></TableRow>
                  ) : !items.length ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                          No hay {TIPO_LABEL[selected.familia].toLowerCase()}s Inactivo(a)s en esta sucursal.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((row) => (
                      <TableRow key={row.key} hover>
                        <TableCell>{row.identificador}</TableCell>
                        <TableCell>{row.modelo}</TableCell>
                        <TableCell>{row.ciudad}</TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            disabled={asignando === row.key}
                            onClick={() => handleAsignar(row)}
                            sx={{ minHeight: 22, height: 22, fontSize: 10.5 }}
                          >
                            {asignando === row.key ? <CircularProgress size={14} color="inherit" /> : "Asignar"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                Página {total ? page : 0} / {total ? totalPages : 0}
              </Typography>
              <Button size="small" variant="outlined" startIcon={<NavigateBeforeIcon />} disabled={loadingItems || page <= 1}
                onClick={() => cargarDisponibles(selected.familia, page - 1, buscarAplicado)} sx={{ minHeight: 24, height: 24, fontSize: 10.5 }}>
                Anterior
              </Button>
              <Button size="small" variant="outlined" endIcon={<NavigateNextIcon />} disabled={loadingItems || page >= totalPages}
                onClick={() => cargarDisponibles(selected.familia, page + 1, buscarAplicado)} sx={{ minHeight: 24, height: 24, fontSize: 10.5 }}>
                Siguiente
              </Button>
            </Stack>
          </Box>
        </Paper>
      )}

      {agregarAbierto && (
        <Paper ref={panelRef} variant="outlined" sx={{ mb: 2, borderColor: "primary.main" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Agregar equipo a reemplazar
            </Typography>
            <IconButton size="small" onClick={() => setAgregarAbierto(false)}><CloseIcon fontSize="small" /></IconButton>
          </Stack>

          <Box sx={{ px: 1.5, pb: 1.5 }}>
            {tiposAgregar.length > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  color="primary"
                  value={agregarTipo}
                  onChange={(e, nuevo) => {
                    if (!nuevo) return;
                    setAgregarTipo(nuevo);
                    setAgregarBuscar("");
                    setAgregarBuscarAplicado("");
                    cargarDisponiblesAgregar(nuevo, 1, "");
                  }}
                  sx={{ height: 30 }}
                >
                  {tiposAgregar.map((t) => (
                    <ToggleButton key={t} value={t} sx={{ fontSize: 11.5, px: 1.5, py: 0.35, textTransform: "none" }}>
                      {TIPO_LABEL[t]}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            )}

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <TextField
                size="small"
                label="Buscar"
                value={agregarBuscar}
                onChange={(e) => setAgregarBuscar(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleBuscarAgregar(); }}
                sx={{ minWidth: 160, ...controlSx }}
              />
              <IconButton size="small" onClick={handleBuscarAgregar} title="Buscar"><SearchIcon fontSize="small" /></IconButton>
              <Chip size="small" label={`Total: ${agregarTotal}`} sx={{ height: 20, fontSize: 11 }} />
            </Stack>

            <TableContainer component={Paper} variant="outlined" sx={{ mb: 0.5, maxHeight: 280, borderRadius: 0.5 }}>
              <Table size="small" stickyHeader sx={tableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Identificador</TableCell>
                    <TableCell sx={headCellSx}>Modelo</TableCell>
                    <TableCell sx={{ ...headCellSx, width: 90 }}>Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {agregarLoading ? (
                    <TableRow><TableCell colSpan={3} align="center" sx={{ py: 1.5 }}><CircularProgress size={16} /></TableCell></TableRow>
                  ) : !agregarItems.length ? (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                          Este cliente no tiene {(TIPO_LABEL[agregarTipo] || "").toLowerCase()}s activos.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    agregarItems.map((row) => (
                      <TableRow key={row.key} hover>
                        <TableCell>{row.identificador}</TableCell>
                        <TableCell>{row.modelo}</TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            disabled={agregando === row.key}
                            onClick={() => handleAgregar(row)}
                            sx={{ minHeight: 22, height: 22, fontSize: 10.5 }}
                          >
                            {agregando === row.key ? <CircularProgress size={14} color="inherit" /> : "Agregar"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                Página {agregarTotal ? agregarPage : 0} / {agregarTotal ? agregarTotalPages : 0}
              </Typography>
              <Button size="small" variant="outlined" startIcon={<NavigateBeforeIcon />} disabled={agregarLoading || agregarPage <= 1}
                onClick={() => cargarDisponiblesAgregar(agregarTipo, agregarPage - 1, agregarBuscarAplicado)} sx={{ minHeight: 24, height: 24, fontSize: 10.5 }}>
                Anterior
              </Button>
              <Button size="small" variant="outlined" endIcon={<NavigateNextIcon />} disabled={agregarLoading || agregarPage >= agregarTotalPages}
                onClick={() => cargarDisponiblesAgregar(agregarTipo, agregarPage + 1, agregarBuscarAplicado)} sx={{ minHeight: 24, height: 24, fontSize: 10.5 }}>
                Siguiente
              </Button>
            </Stack>
          </Box>
        </Paper>
      )}

      <CentroEquipoDetalleDialog
        open={!!equipoDialog}
        tipo={equipoDialog?.tipo}
        identificador={equipoDialog?.identificador}
        onClose={() => setEquipoDialog(null)}
      />
    </Box>
  );
};

export default AsignarReemplazoOT;
